// All computed state. Pure functions — no React, no DOM, fully testable.

import {
  SPRINT,
  GATES,
  EVIDENCE_BY_ID,
  REGULATED_ACTIVITIES,
  INFRA_CASH_STAGE_WEIGHT,
  SAUDI_SIGNALS,
  D001_STAGES,
  D001_STAGE_EXIT,
} from './constants';
import {
  addDays,
  businessDaysBetween,
  clamp,
  diffDays,
  fmtDate,
  isAfter,
  todayISO,
  weekEnd,
  weekStart,
  withinRange,
} from './dates';
import { deriveFunnel } from './funnel';
import { deriveActions } from './actions';
import { measure, isUnknown } from './measure';
import { sprintStartOf, sprintIntegrity } from './sprint';
import { orgStrategyApprovedAt, orgTimeTargets } from './orgPlan';

// Note: deriveGateAccountability is composed in the store after deriveAll to avoid
// a circular import (gates → derive.weightOf ← gateAccountability ← derive).

export function currentDate(state, now) {
  if (state?.settings && state.settings.useRealClock === false) {
    return state.settings.pinnedDate || resolveSprintBounds(state).start;
  }
  return todayISO(now);
}

/**
 * Sprint calendar from Sprint Initialization (T0).
 * End date and gates are T+(totalDays-1) / T+(gate.day-1).
 */
export function resolveSprintBounds(state) {
  const integrity = sprintIntegrity(state);
  const start = sprintStartOf(state);
  const end = addDays(start, SPRINT.totalDays - 1);
  const gates = GATES.map((g) => {
    const date = addDays(start, g.day - 1);
    return {
      day: g.day,
      key: g.key,
      date,
      label: `DAY ${g.day} — ${fmtDate(date).toUpperCase()}`,
    };
  });
  const rangeLabel = `${fmtDate(start)} → ${fmtDate(end)}`;
  const strategyApprovedAt = orgStrategyApprovedAt(state);
  const lockLabel = strategyApprovedAt
    ? `STRATEGY LOCKED · APPROVED ${fmtDate(strategyApprovedAt).toUpperCase()}`
    : 'PLAN NOT SET';
  return {
    start,
    end,
    totalDays: SPRINT.totalDays,
    rangeLabel,
    label: `${fmtDate(start, { year: true })} → ${fmtDate(end, { year: true })}`,
    strategyApprovedAt,
    lockLabel,
    executionLabel: `EXECUTION SPRINT · ${fmtDate(start).toUpperCase()} → ${fmtDate(end).toUpperCase()}`,
    rebaselineLabel: integrity.rebaselined ? integrity.rebaselineLabel : '',
    integrity,
    gates,
  };
}

// ---------------------------------------------------------------- sprint ---

export function deriveSprint(state, now) {
  const bounds = resolveSprintBounds(state);
  const today = currentDate(state, now);
  const rawDay = diffDays(bounds.start, today) + 1;
  const day = clamp(rawDay, 1, bounds.totalDays);
  const phase = rawDay < 1 ? 'pre' : rawDay > bounds.totalDays ? 'post' : 'active';
  const daysCompleted = clamp(rawDay - 1, 0, bounds.totalDays);
  const daysRemaining = clamp(bounds.totalDays - daysCompleted, 0, bounds.totalDays);
  const nextGate = bounds.gates.find((g) => g.day >= day) || null;
  const daysToGate = nextGate ? nextGate.day - day : null;
  const calendarDaysToGate = nextGate ? Math.max(0, diffDays(today, nextGate.date)) : null;
  const daysUntilStart = Math.max(0, diffDays(today, bounds.start));
  // Overdue / aging / time-pressure only after execution begins.
  const executionAsOf = phase === 'pre' ? null : today;
  return {
    today,
    executionAsOf,
    day,
    rawDay,
    phase,
    daysCompleted,
    daysRemaining,
    daysUntilStart,
    nextGate,
    daysToGate,
    calendarDaysToGate,
    pctElapsed: Math.round((daysCompleted / bounds.totalDays) * 100),
    weekStart: weekStart(today),
    weekEnd: weekEnd(today),
    start: bounds.start,
    end: bounds.end,
    totalDays: bounds.totalDays,
    rangeLabel: bounds.rangeLabel,
    label: bounds.label,
    lockLabel: bounds.lockLabel,
    executionLabel: bounds.executionLabel,
    strategyApprovedAt: bounds.strategyApprovedAt,
    rebaselineLabel: bounds.rebaselineLabel,
    integrity: bounds.integrity,
    gates: bounds.gates,
    dateForGate: (d) => addDays(bounds.start, d - 1),
  };
}

// ---------------------------------------------------------------- runway ---

export function deriveRunway(state, sprint) {
  const r = state.runway || {};
  const th = state.settings?.alertThresholds || {};
  // Accept legacy aliases so UI cashToday and API currentCash stay one truth.
  const cashRaw = r.cashToday ?? r.currentCash ?? r.cash;
  const burnRaw = r.monthlyBurn ?? r.burn;
  const cashM = measure(cashRaw);
  const burnM = measure(burnRaw);
  const minBurnM = measure(r.minViableBurn);
  const baselineM = measure(r.baselineMonthlyBurn);
  const cashUnknown = isUnknown(cashM);
  const burnUnknown = isUnknown(burnM);
  const minBurnUnknown = isUnknown(minBurnM);
  const cash = cashUnknown ? null : cashM.value;
  const burn = burnUnknown ? null : burnM.value;
  const minBurn = minBurnUnknown ? null : minBurnM.value;
  const baselineUnknown = isUnknown(baselineM);
  const baseline = baselineUnknown ? (burnUnknown ? null : burn) : baselineM.value;

  // Current runway = cash ÷ current burn. Confirmed-zero burn → infinite runway.
  // Missing cash or burn → Unknown (never coerce to 0 months).
  const months =
    cash !== null && burn !== null ? (burn > 0 ? cash / burn : Infinity) : null;
  const minMonths =
    cash !== null && minBurn !== null ? (minBurn > 0 ? cash / minBurn : Infinity) : null;

  const missingInputs = [];
  if (cashUnknown) missingInputs.push('Cash on hand');
  if (burnUnknown) missingInputs.push('current monthly burn');
  if (minBurnUnknown) missingInputs.push('minimum viable monthly burn');
  const missingForCurrent = missingInputs.filter((l) => l !== 'minimum viable monthly burn');

  const watch = th.runwayWatchMonths ?? 6;
  const critical = th.runwayCriticalMonths ?? 3;
  let status = 'unknown';
  if (cashUnknown || burnUnknown) status = 'unknown';
  else if (months !== null) {
    if (!Number.isFinite(months) || months >= watch) status = 'healthy';
    else if (months >= critical) status = 'watch';
    else status = 'critical';
  }

  const expenses = state.expenses || [];
  const thisWeek = expenses.filter((e) => e.date && withinRange(e.date, sprint.weekStart, sprint.weekEnd));
  const spendThisWeek = sum(thisWeek.map((e) => num(e.amount)));
  const sprintSpend = expenses.filter((e) => e.date && withinRange(e.date, sprint.start, sprint.end));
  const proofSpend = sum(sprintSpend.filter((e) => e.category === 'PROVE').map((e) => num(e.amount)));
  const stopSpend = sum(sprintSpend.filter((e) => e.category === 'STOP').map((e) => num(e.amount)));
  const totalSprintSpend = sum(sprintSpend.map((e) => num(e.amount)));
  const proofShare = totalSprintSpend > 0 ? Math.round((proofSpend / totalSprintSpend) * 100) : null;

  // Cash saved = burn reduction achieved since the sprint began, pro-rated.
  const monthsElapsed = sprint.daysCompleted / 30.44;
  const cashSaved =
    baseline !== null && burn !== null ? Math.max(0, baseline - burn) * monthsElapsed : null;

  const unjustified = expenses.filter(
    (e) => num(e.amount) >= (th.materialSpend ?? 5000) && !String(e.evidenceObjective || '').trim()
  );

  const runwayEndsOn =
    months !== null && Number.isFinite(months)
      ? addDays(sprint.today, Math.floor(months * 30.44))
      : null;

  return {
    cash,
    burn,
    minBurn,
    baseline,
    cashKind: cashM.kind,
    burnKind: burnM.kind,
    baselineKind: baselineM.kind,
    cashUnknown,
    burnUnknown,
    minBurnUnknown,
    baselineUnknown: baselineUnknown && burnUnknown,
    missingInputs,
    missingForCurrent,
    months,
    minMonths,
    status,
    spendThisWeek,
    proofSpend,
    stopSpend,
    totalSprintSpend,
    proofShare,
    cashSaved,
    unjustified,
    runwayEndsOn,
    survivesSprint: months === null ? null : months * 30.44 >= sprint.daysRemaining,
  };
}

/** Truth-strip / card copy: primary months (or Unknown) + what is missing or the min-burn line. */
export function describeRunway(runway) {
  if (runway.months === null || runway.months === undefined) {
    const missing = runway.missingForCurrent || [];
    return {
      primary: 'Unknown',
      secondary: missing.length ? `Missing: ${missing.join(', ')}` : 'Insufficient data to calculate runway',
    };
  }
  if (runway.minMonths !== null && runway.minMonths !== undefined) {
    return {
      primary: fmtMonths(runway.months),
      secondary: `Minimum-burn runway: ${fmtMonths(runway.minMonths)}`,
    };
  }
  return {
    primary: fmtMonths(runway.months),
    secondary: runway.minBurnUnknown
      ? 'Missing: minimum viable monthly burn'
      : '',
  };
}

// -------------------------------------------------------------- evidence ---

export function deriveEvidence(state, sprint) {
  const all = [...(state.evidence || [])].sort((a, b) => (a.date < b.date ? 1 : -1));
  const proof = all.filter((e) => e.level && e.level !== 'ACT');
  const thisWeek = all.filter((e) => e.date && withinRange(e.date, sprint.weekStart, sprint.weekEnd));
  const byLevel = countBy(proof, (e) => e.level);
  const byMarket = {};
  for (const e of proof) {
    const m = e.market || 'Unassigned';
    byMarket[m] = byMarket[m] || { total: 0, E4: 0, E5: 0, items: [] };
    byMarket[m].total += 1;
    if (e.level === 'E4') byMarket[m].E4 += 1;
    if (e.level === 'E5') byMarket[m].E5 += 1;
    byMarket[m].items.push(e);
  }
  const weekProof = thisWeek.filter((e) => e.level && e.level !== 'ACT');
  return {
    all,
    proof,
    activities: all.filter((e) => e.level === 'ACT'),
    thisWeek,
    weekProof,
    byLevel,
    byMarket,
    e4: byLevel.E4 || 0,
    e5: byLevel.E5 || 0,
    e4Week: weekProof.filter((e) => e.level === 'E4').length,
    e5Week: weekProof.filter((e) => e.level === 'E5').length,
    highest: proof.reduce((acc, e) => Math.max(acc, weightOf(e.level)), -1),
  };
}

export function weightOf(levelId) {
  return EVIDENCE_BY_ID[levelId]?.weight ?? -1;
}

export function highestEvidenceFor(state, predicate) {
  let best = null;
  for (const e of state.evidence || []) {
    if (e.level === 'ACT') continue;
    if (!predicate(e)) continue;
    if (!best || weightOf(e.level) > weightOf(best.level)) best = e;
  }
  return best;
}

// ----------------------------------------------------------------- D001 ----

export function deriveD001(state, sprint) {
  const d = state.d001 || {};
  const sponsor = d.sponsor || {};
  const asset = d.asset || {};

  const assetChecks = [
    { key: 'authority', label: 'Sponsor authority confirmed', ok: !!sponsor.authorityConfirmed },
    { key: 'named', label: 'Named asset exists', ok: !!asset.namedAssetExists },
    { key: 'title', label: 'Ownership / title information available', ok: !!asset.titleInfoAvailable },
    { key: 'economics', label: 'Basic economics understood', ok: !!asset.economicsUnderstood },
    { key: 'screen', label: 'Asset passes initial screening', ok: !!asset.passesScreen },
    { key: 'next', label: 'Sponsor has agreed a next step', ok: !!sponsor.agreedNextStep },
  ];
  const assetMet = assetChecks.filter((c) => c.ok).length;
  const assetGate = assetMet === assetChecks.length ? 'green' : assetMet >= 3 ? 'amber' : 'red';

  const reg = d.regulated || {};
  const activities = REGULATED_ACTIVITIES.map((a) => ({
    ...a,
    responsible: reg[a.key]?.responsible || 'Unknown',
    note: reg[a.key]?.note || '',
  }));
  const unknown = activities.filter((a) => a.responsible === 'Unknown');
  const criticalUnknown = unknown.filter((a) => a.critical);
  const partners = d.partners || [];
  const partnerReady = partners.filter((p) => p.stage === 'Pilot Ready' || p.stage === 'Active');
  const partnerEngaged = partners.filter((p) =>
    ['Architecture Review', 'Commercial Review', 'Legal / Compliance'].includes(p.stage)
  );

  let executionGate = 'red';
  if (criticalUnknown.length === 0 && partnerReady.length > 0) executionGate = 'green';
  else if (partnerEngaged.length > 0 || criticalUnknown.length < REGULATED_ACTIVITIES.filter((a) => a.critical).length)
    executionGate = 'amber';
  const executionChecks = [
    {
      key: 'unknowns',
      label: 'No critical activity left Unknown',
      ok: criticalUnknown.length === 0,
      detail: criticalUnknown.length ? `${criticalUnknown.length} critical activities unassigned` : '',
    },
    {
      key: 'partner',
      label: 'At least one partner at Pilot Ready or Active',
      ok: partnerReady.length > 0,
      detail: partners.length ? `${partners.length} partner(s) tracked` : 'No partner identified',
    },
  ];

  const cap = d.capital || {};
  const convos = cap.conversations || [];
  const credible = convos.filter((c) => c.credible !== false);
  const acceptance = convos.filter((c) => c.acceptanceSignal);
  const capitalChecks = [
    { key: 'segment', label: 'Target investor segment defined', ok: !!String(cap.targetSegment || '').trim() },
    { key: 'ticket', label: 'Ticket hypothesis defined', ok: !!String(cap.expectedTicket || '').trim() },
    {
      key: 'convos',
      label: 'At least two credible capital / distribution conversations',
      ok: credible.length >= 2,
      detail: `${credible.length} recorded`,
    },
    {
      key: 'acceptance',
      label: 'At least one D001-specific acceptance signal',
      ok: acceptance.length >= 1,
      detail: `${acceptance.length} recorded`,
    },
  ];
  const capMet = capitalChecks.filter((c) => c.ok).length;
  const capitalGate = capMet === capitalChecks.length ? 'green' : capMet >= 2 ? 'amber' : 'red';

  const stageIndex = Math.max(0, D001_STAGES.indexOf(d.stage || 'Target'));
  const exitRule = D001_STAGE_EXIT[d.stage] || null;
  const dubaiEvidence = (state.evidence || []).filter((e) => e.market === 'Dubai' && e.level !== 'ACT');
  const highest = dubaiEvidence.reduce((acc, e) => Math.max(acc, weightOf(e.level)), -1);
  const canAdvance = !exitRule ? true : highest >= weightOf(exitRule.minLevel);

  const constraint = d.constraint || {};
  const overdue =
    sprint.executionAsOf && constraint.due ? isAfter(sprint.executionAsOf, constraint.due) : false;

  return {
    d,
    sponsor,
    asset,
    assetChecks,
    assetGate,
    activities,
    unknown,
    criticalUnknown,
    partners,
    partnerReady,
    executionGate,
    executionChecks,
    capitalChecks,
    capitalGate,
    capitalConvos: convos,
    stage: d.stage || 'Target',
    stageIndex,
    exitRule,
    canAdvance,
    highestEvidence: highest,
    constraint,
    constraintOverdue: overdue,
    evidenceLevel: highest >= 0 ? levelIdFromWeight(highest) : 'E0',
    // Revision 01: D001 exists once an opportunity holds the Primary
    // designation. The legacy flag still counts so nothing disappears.
    exists: !!(d.exists || d.primaryOpportunityId),
    primaryOpportunityId: d.primaryOpportunityId || '',
  };
}

export function levelIdFromWeight(w) {
  const found = Object.values(EVIDENCE_BY_ID).find((e) => e.weight === w);
  return found ? found.id : 'E0';
}

// ---------------------------------------------------------------- Saudi ----

export function deriveSaudi(state, sprint) {
  const s = state.saudi || {};
  const signals = SAUDI_SIGNALS.map((def) => {
    const value = s.signals?.[def.key] || 'none';
    const note = s.signalNotes?.[def.key] || '';
    const supported = !!String(note).trim();
    return { ...def, value, note, supported, unsupported: value !== 'none' && !supported };
  });
  const strong = signals.filter((x) => x.value === 'strong').length;
  const weak = signals.filter((x) => x.value === 'weak').length;

  let verdict = 'Park';
  if (strong >= 3) verdict = 'Concentrate';
  else if (strong === 2) verdict = 'Continue selectively';

  const pipeA = s.offers?.A?.pipeline || {};
  const pipeB = s.offers?.B?.pipeline || {};
  const paid = num(pipeA.paid) + num(pipeB.paid);
  const proposals = num(pipeA.proposals) + num(pipeB.proposals);
  const budget = num(pipeA.budget) + num(pipeB.budget);
  const meetings = num(pipeA.meetings) + num(pipeB.meetings);
  const painConfirmed = num(pipeA.painConfirmed) + num(pipeB.painConfirmed);

  return {
    s,
    signals,
    strong,
    weak,
    count: strong,
    verdict,
    verdictAt: sprint.day >= 45 ? 'final' : sprint.day >= 30 ? 'day30' : 'interim',
    totals: { paid, proposals, budget, meetings, painConfirmed },
    unsupported: signals.filter((x) => x.unsupported),
  };
}

// -------------------------------------------------------- Infrastructure ---

export function deriveInfra(state, sprint) {
  const opps = state.infra?.opportunities || [];
  const live = opps.filter((o) => o.stage !== 'Lost / Parked');
  const stageCount = (name) => opps.filter((o) => o.stage === name).length;

  const qualified = live.filter((o) => !['Target'].includes(o.stage)).length;
  const demos = live.filter((o) => weightOf(o.evidenceLevel) >= 1).length;
  const discoveries = live.filter((o) =>
    ['Technical Discovery', 'POC Proposed', 'Commercial Review', 'Contract / POC', 'Deployment', 'Expansion'].includes(
      o.stage
    )
  ).length;
  const proposals = live.filter((o) =>
    ['POC Proposed', 'Commercial Review', 'Contract / POC', 'Deployment', 'Expansion'].includes(o.stage)
  ).length;
  const paidPocs = live.filter((o) => o.paidPoc).length;
  const contracts = live.filter((o) => ['Contract / POC', 'Deployment', 'Expansion'].includes(o.stage)).length;
  const committedValue = sum(
    live
      .filter((o) => ['Contract / POC', 'Deployment', 'Expansion'].includes(o.stage))
      .map((o) => num(o.proposalValue))
  );

  const scored = live
    .map((o) => ({
      o,
      score:
        (INFRA_CASH_STAGE_WEIGHT[o.stage] ?? 0) * 10 +
        Math.max(0, weightOf(o.evidenceLevel)) * 3 +
        (o.budget ? 4 : 0) +
        (o.paidPoc ? 5 : 0),
    }))
    .sort((a, b) => b.score - a.score);

  return {
    opps,
    live,
    stageCount,
    qualified,
    demos,
    discoveries,
    proposals,
    paidPocs,
    contracts,
    committedValue,
    closest: scored[0]?.o || null,
    ranked: scored.map((x) => x.o),
    features: state.infra?.features || [],
    customWithoutDeal: (state.infra?.features || []).filter(
      (f) => f.classification === 'CUSTOM' && !String(f.opportunityId || '').trim()
    ),
  };
}

// ------------------------------------------------------------ founder time -

export function deriveTime(state, sprint) {
  const log = state.timeLog || [];
  const week = log.find((w) => w.weekStart === sprint.weekStart) || null;
  const recorded = !!week;
  const targets = orgTimeTargets(state);
  const totals = targets.map((t) => ({ ...t, hours: recorded ? num(week?.[t.key]) : 0 }));
  const totalHours = recorded ? sum(totals.map((t) => t.hours)) : 0;
  const hasMinimumHours = recorded && totalHours > 0;
  const rows = totals.map((t) => ({
    ...t,
    actual: hasMinimumHours ? Math.round((t.hours / totalHours) * 100) : null,
    delta: hasMinimumHours ? Math.round((t.hours / totalHours) * 100) - t.target : null,
  }));
  const externalKeys = new Set(
    targets.filter((t) => t.key !== 'other').map((t) => t.key),
  );
  const external = rows.filter((r) => externalKeys.has(r.key)).reduce((a, r) => a + r.hours, 0);
  const internalShare = hasMinimumHours ? Math.round(((totalHours - external) / totalHours) * 100) : null;
  const primary = rows[0];
  const drifting =
    hasMinimumHours && primary && primary.target >= 40 && (primary.actual ?? 0) < primary.target - 15;
  return {
    week,
    rows,
    totalHours,
    external,
    internalShare,
    drifting,
    recorded,
    hasMinimumHours,
    label: recorded ? null : targets.length ? 'No time data recorded' : 'Plan not set',
  };
}

// ---------------------------------------------------------------- actions ---

export function deriveTop3(state, sprint, ctx = {}) {
  return deriveActions(state, sprint, ctx);
}

// ---------------------------------------------------------------- alerts ---

export function deriveAlerts(state, ctx) {
  const { sprint, runway, d001, infra, evidence, funnel, top3 } = ctx;
  const th = state.settings?.alertThresholds || {};
  const out = [];
  const push = (id, severity, title, detail, goto) => out.push({ id, severity, title, detail, goto });

  // 1. Tier-A opportunity has no next action.
  for (const o of infra.live.filter((x) => x.tier === 'A' && !String(x.nextAction || '').trim())) {
    push(`tierA-noaction-${o.id}`, 'high', 'Tier-A opportunity has no next action', `${o.account || 'Unnamed account'} is Tier-A and has no next founder action.`, 'infrastructure');
  }

  // 2. D001 blocker overdue.
  if (d001.constraintOverdue) {
    push('d001-blocker-overdue', 'high', 'D001 blocker overdue', `"${d001.constraint.blocker}" was due ${d001.constraint.due}.`, 'dubai');
  }

  // 3. E4 opportunity has no founder follow-up.
  for (const o of infra.live.filter((x) => weightOf(x.evidenceLevel) >= 4)) {
    const asOf = sprint.executionAsOf;
    const overdueDue = asOf && o.nextActionDue && isAfter(asOf, o.nextActionDue);
    const stale = !String(o.nextAction || '').trim() || !!overdueDue;
    if (stale)
      push(`e4-nofollow-${o.id}`, 'high', 'E4 opportunity has no founder follow-up', `${o.account || 'Unnamed account'} reached ${o.evidenceLevel} and the next action is missing or overdue.`, 'infrastructure');
  }

  // 4. Proposal idle more than N business days.
  for (const o of infra.live.filter((x) => ['POC Proposed', 'Commercial Review'].includes(x.stage))) {
    const asOf = sprint.executionAsOf;
    if (!asOf) continue;
    const idle = o.lastTouch ? businessDaysBetween(o.lastTouch, asOf) : null;
    if (idle !== null && idle > (th.idleProposalDays ?? 5))
      push(`prop-idle-${o.id}`, 'high', 'Proposal idle', `${o.account || 'Unnamed account'} — proposal untouched for ${idle} business days.`, 'infrastructure');
  }

  // 5. Tier-A account untouched for more than N business days.
  for (const o of infra.live.filter((x) => x.tier === 'A')) {
    const asOf = sprint.executionAsOf;
    if (!asOf) continue;
    const idle = o.lastTouch ? businessDaysBetween(o.lastTouch, asOf) : null;
    if (idle !== null && idle > (th.idleTierADays ?? 5))
      push(`tierA-idle-${o.id}`, 'medium', 'Tier-A account untouched', `${o.account || 'Unnamed account'} — no contact for ${idle} business days.`, 'infrastructure');
  }

  // 6. Product work exists without a commercial opportunity.
  for (const f of infra.customWithoutDeal) {
    push(`custom-nodeal-${f.id}`, 'high', 'Product work without commercial proof', `"${f.name}" is classified CUSTOM with no opportunity attached.`, 'infrastructure');
  }
  for (const p of (state.productRequests || []).filter((r) => r.classification === 'FROZEN')) {
    push(`prod-frozen-${p.id}`, 'medium', 'Frozen product request', `"${p.request}" is not attached to commercial proof.`, 'infrastructure');
  }

  // 7. Material spend lacks an evidence objective.
  for (const e of runway.unjustified) {
    push(`spend-noevidence-${e.id}`, 'high', 'Material spend lacks an evidence objective', `${e.label || 'Unlabelled expense'} — ${fmtMoney(e.amount)} with no evidence objective.`, 'runway');
  }

  // 8. Regulator-sensitive communication waiting for approval.
  for (const a of (state.approvals || []).filter((x) => x.status === 'Pending')) {
    push(`approval-${a.id}`, 'high', 'Regulator-sensitive communication awaiting approval', `${a.label || 'Untitled'} — held since ${a.date || 'unknown date'}.`, 'settings');
  }

  // 9. Runway threshold crossed.
  if (runway.status === 'critical')
    push('runway-critical', 'critical', 'Runway threshold crossed', `${fmtMonths(runway.months)} of runway at current burn.`, 'runway');
  else if (runway.status === 'watch')
    push('runway-watch', 'medium', 'Runway entering watch band', `${fmtMonths(runway.months)} of runway at current burn.`, 'runway');
  if (runway.survivesSprint === false)
    push(
      'runway-sprint',
      'critical',
      'Runway does not cover the sprint',
      `Current cash and burn do not reach ${fmtDate(sprint.end)}.`,
      'runway',
    );

  // 10. Strategy Exception requested.
  for (const x of (state.exceptions || []).filter((e) => !e.decision || e.decision === 'Pending')) {
    push(`exception-${x.id}`, 'high', 'Strategy Exception requested', `${x.trigger} — awaiting decision.`, 'decisions');
  }

  // Sprint-specific: no E4/E5 by day 15 is a signal, not a nag.
  if (sprint.day >= 15 && evidence.e4 + evidence.e5 === 0)
    push('no-e4-day15', 'high', 'No commercial commitment yet', `Day ${sprint.day} of 45 with zero E4 or E5 evidence recorded.`, 'evidence');

  // ---- Revision 01: funnel alerts -----------------------------------------
  if (funnel) {
    for (const a of funnel.aging) {
      push(a.id, a.severity, a.label, a.detail, a.kind === 'account' ? 'pipeline' : 'pipeline');
    }
    // The first days of the sprint are for building the universe, so the
    // structural funnel warnings hold until day 5 rather than firing at zero.
    const funnelWarmedUp = sprint.day >= 5;
    if (funnelWarmedUp && (funnel.concentration.level === 'Critical' || funnel.concentration.level === 'High'))
      push(
        'concentration',
        funnel.concentration.level === 'Critical' ? 'critical' : 'high',
        `Deal concentration ${funnel.concentration.level.toLowerCase()}`,
        funnel.concentration.why,
        'pipeline'
      );
    if (funnelWarmedUp && funnel.health.status === 'Critical')
      push('funnel-critical', 'critical', 'Funnel health critical', funnel.health.why, 'pipeline');
    else if (funnel.health.status === 'Thin' && sprint.day >= 10)
      push('funnel-thin', 'high', 'Funnel health thin', funnel.health.why, 'pipeline');
    if (sprint.day >= 30 && funnel.counts.primary === 0 && funnel.counts.candidates > 0)
      push(
        'no-primary-day30',
        'high',
        'Candidates exist but none has been designated',
        `Day ${sprint.day}: ${funnel.counts.candidates} D001 candidate(s) and no Primary. Comparison without a decision is drift.`,
        'dubai'
      );
  }

  if (top3) {
    for (const a of top3.overdue || []) {
      push(
        `action-overdue-${a.id}`,
        a.priority === 'Critical' ? 'critical' : 'high',
        `${a.priority} action overdue`,
        `"${a.text}" was due ${a.dueDate}. Unfinished work stays in the execution queue until it is closed.`,
        'actions',
      );
    }
    for (const a of top3.blockedUnnamed || []) {
      push(
        `action-blocker-${a.id}`,
        'high',
        'Blocked action has no named blocker',
        `"${a.text}" is blocked. Name the counterparty, permission, or missing input.`,
        'actions',
      );
    }
    for (const o of top3.missingOppActions || []) {
      push(
        `opp-no-next-${o.id}`,
        'high',
        'Active E3/E4 opportunity has no next action',
        `${o.name || o.accountName || 'Unnamed opportunity'} is at ${o.evidenceLevel} with no dated next founder action.`,
        'pipeline',
      );
    }
    if ((top3.lowValue || []).length >= 3) {
      push(
        'actions-low-value',
        'medium',
        'Low-value activity',
        `${top3.lowValue.length} completed commercial actions produced no commercial proof. Judge evidence-producing moves against their intended result — not preparation work.`,
        'actions',
      );
    }
  }

  const rank = { critical: 0, high: 1, medium: 2 };
  return out
    .filter((a) => !(state.ui?.dismissedAlerts || []).includes(a.id))
    .sort((a, b) => rank[a.severity] - rank[b.severity]);
}

// --------------------------------------------------------------- helpers ---

export function num(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[, ]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function sum(arr) {
  return arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
}

export function countBy(arr, fn) {
  const out = {};
  for (const x of arr) {
    const k = fn(x);
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

export function fmtMoney(v, currency) {
  if (v === null || v === undefined || v === '') return 'Unknown';
  const n = num(v);
  const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString('en-US') : String(Math.round(n * 100) / 100);
  return currency ? `${currency} ${s}` : s;
}

export function fmtMonths(m) {
  if (m === null || m === undefined) return 'Unknown';
  if (!Number.isFinite(m)) return '∞';
  return `${Math.round(m * 10) / 10} months`;
}

// One call to build the whole derived world.
export function deriveAll(state, now) {
  const sprint = deriveSprint(state, now);
  const runway = deriveRunway(state, sprint);
  const evidence = deriveEvidence(state, sprint);
  const d001 = deriveD001(state, sprint);
  const saudi = deriveSaudi(state, sprint);
  const infra = deriveInfra(state, sprint);
  const time = deriveTime(state, sprint);
  const funnel = deriveFunnel(state, sprint);
  const top3 = deriveTop3(state, sprint, { d001, funnel, time });
  const ctx = { sprint, runway, evidence, d001, saudi, infra, time, top3, funnel };
  ctx.alerts = deriveAlerts(state, ctx);
  return ctx;
}
